/**
 * TruBeauté — Affirming Resources directory data.
 *
 * Why a flat data file instead of a CMS / DB
 * ──────────────────────────────────────────
 * This list will grow slowly (~5 new orgs / month at most) and needs
 * to ship statically with the SPA so the page is instant + SEO-able.
 * Keeping it as a typed JS module means we get:
 *   • Free CDN caching with the bundle
 *   • Type-safety from the resource shape comment below
 *   • Diffs on every founder review (PR-able)
 *   • Zero server round-trip for filtering / search
 *
 * If we ever exceed ~200 orgs or need founder-side editing, migrate
 * this to MongoDB (`affirming_resources` collection) + a tiny
 * `/api/resources/list` endpoint. Page UI stays the same.
 *
 * Resource shape
 * ──────────────
 *   id           — kebab-case slug, unique across the file
 *   country      — one of COUNTRY_CODES below
 *   category     — one of CATEGORY_CODES below
 *   name         — org's official name (NEVER translated — orgs own their identity)
 *   url          — primary site
 *   description  — { en, fr?, es?, pt? } — EN required, others optional
 *                  Locales fall back to EN when missing.
 *   languages    — locales the ORG itself operates in (informational, not for UI filtering)
 *   lastReviewed — ISO date of last founder review (yearly link-check cadence)
 *
 * Adding a new resource (founder checklist)
 *   1. Confirm the org is currently active (site loads, last news < 12mo)
 *   2. Confirm gender-affirming mission alignment (no exclusionary policies)
 *   3. Add the row below, alphabetical within its country block
 *   4. Update lastReviewed when the link is re-verified
 */

export const COUNTRY_CODES = [
  "global",
  "us",
  "ca",
  "uk",
  "fr",
  "es",
  "mx",
  "br",
  "au",
  "nz",
];

export const CATEGORY_CODES = [
  "healthcare",
  "financial",
  "legal",
  "mentalHealth",
  "crisis",
  "community",
  "employment",
  "voiceTraining",
];

// Flag emoji per country, used for the country-section heading. Global
// uses a globe glyph. Keep this in sync with COUNTRY_CODES.
export const COUNTRY_FLAGS = {
  global: "🌎",
  us: "🇺🇸",
  ca: "🇨🇦",
  uk: "🇬🇧",
  fr: "🇫🇷",
  es: "🇪🇸",
  mx: "🇲🇽",
  br: "🇧🇷",
  au: "🇦🇺",
  nz: "🇳🇿",
};

// Founder's first directory — Feb 9, 2026.
export const RESOURCES = [
  // ── 🌎 Global ─────────────────────────────────────────────────────
  {
    id: "point-of-pride",
    country: "global",
    category: "financial",
    name: "Point of Pride",
    url: "https://www.pointofpride.org",
    description: {
      en: "Provides gender-affirming care assistance programs including surgery funds, HRT access, electrolysis support, binders, shapewear, and other transition-related assistance.",
      fr: "Programmes d'aide aux soins d'affirmation de genre : fonds chirurgicaux, accès aux THS, électrolyse, binders, sous-vêtements affirmants et autres soutiens liés à la transition.",
      es: "Programas de asistencia para atención de afirmación de género: fondos quirúrgicos, acceso a TRH, electrólisis, binders, ropa moldeadora y otros apoyos relacionados con la transición.",
      pt: "Programas de assistência em cuidados de afirmação de gênero: fundos para cirurgias, acesso à TRH, eletrólise, binders, modeladores e outros apoios à transição.",
    },
    languages: ["en"],
    lastReviewed: "2026-02-09",
  },
  {
    id: "trans-lifeline",
    country: "global",
    category: "crisis",
    name: "Trans Lifeline",
    url: "https://translifeline.org",
    description: {
      en: "Peer support hotline run by and for transgender people.",
      fr: "Ligne d'écoute par les pairs, gérée par et pour les personnes transgenres.",
      es: "Línea de apoyo entre pares dirigida por y para personas transgénero.",
      pt: "Linha de apoio entre pares operada por e para pessoas transgênero.",
    },
    languages: ["en", "es"],
    lastReviewed: "2026-02-09",
  },
  {
    id: "trevor-project",
    country: "global",
    category: "crisis",
    name: "The Trevor Project",
    url: "https://www.thetrevorproject.org",
    description: {
      en: "Crisis intervention and suicide prevention services for LGBTQ+ youth.",
      fr: "Intervention de crise et prévention du suicide pour les jeunes LGBTQ+.",
      es: "Intervención en crisis y prevención del suicidio para jóvenes LGBTQ+.",
      pt: "Intervenção em crise e prevenção do suicídio para jovens LGBTQ+.",
    },
    languages: ["en", "es"],
    lastReviewed: "2026-02-09",
  },
  {
    id: "wpath",
    country: "global",
    category: "healthcare",
    name: "WPATH",
    url: "https://www.wpath.org",
    description: {
      en: "World Professional Association for Transgender Health. International standards of care and provider resources.",
      fr: "Association mondiale des professionnels pour la santé transgenre. Normes de soins internationales et ressources pour praticiens.",
      es: "Asociación Mundial de Profesionales para la Salud Transgénero. Estándares de atención internacionales y recursos para profesionales.",
      pt: "Associação Mundial de Profissionais para a Saúde Transgênero. Padrões internacionais de cuidado e recursos para profissionais.",
    },
    languages: ["en"],
    lastReviewed: "2026-02-09",
  },
  {
    id: "ilga-world",
    country: "global",
    category: "community",
    name: "ILGA World",
    url: "https://ilga.org",
    description: {
      en: "Global federation advocating for LGBTQIA+ rights worldwide.",
      fr: "Fédération mondiale qui défend les droits LGBTQIA+ dans le monde entier.",
      es: "Federación mundial que aboga por los derechos LGBTQIA+ en todo el mundo.",
      pt: "Federação global que defende os direitos LGBTQIA+ em todo o mundo.",
    },
    languages: ["en", "es", "fr", "pt"],
    lastReviewed: "2026-02-09",
  },

  // ── 🇺🇸 United States ─────────────────────────────────────────────
  {
    id: "planned-parenthood",
    country: "us",
    category: "healthcare",
    name: "Planned Parenthood",
    url: "https://www.plannedparenthood.org",
    description: {
      en: "Gender-affirming healthcare services at many locations.",
    },
    languages: ["en", "es"],
    lastReviewed: "2026-02-09",
  },
  {
    id: "glma",
    country: "us",
    category: "healthcare",
    name: "GLMA Provider Directory",
    url: "https://glma.org",
    description: {
      en: "Directory of LGBTQ+ affirming healthcare providers.",
    },
    languages: ["en"],
    lastReviewed: "2026-02-09",
  },
  {
    id: "ucsf-transcare",
    country: "us",
    category: "healthcare",
    name: "UCSF Transgender Care",
    url: "https://transcare.ucsf.edu",
    description: {
      en: "Educational resources and gender-affirming healthcare guidance.",
    },
    languages: ["en"],
    lastReviewed: "2026-02-09",
  },
  {
    id: "genderbands",
    country: "us",
    category: "financial",
    name: "Genderbands",
    url: "https://www.genderbands.org",
    description: {
      en: "Support for access to gender-affirming care.",
    },
    languages: ["en"],
    lastReviewed: "2026-02-09",
  },
  {
    id: "transgender-law-center",
    country: "us",
    category: "legal",
    name: "Transgender Law Center",
    url: "https://transgenderlawcenter.org",
    description: {
      en: "Legal advocacy and educational resources.",
    },
    languages: ["en"],
    lastReviewed: "2026-02-09",
  },

  // ── 🇨🇦 Canada ────────────────────────────────────────────────────
  {
    id: "rainbow-health-ontario",
    country: "ca",
    category: "healthcare",
    name: "Rainbow Health Ontario",
    url: "https://www.rainbowhealthontario.ca",
    description: {
      en: "Healthcare information and provider resources.",
      fr: "Information sur la santé et ressources de praticiens.",
    },
    languages: ["en", "fr"],
    lastReviewed: "2026-02-09",
  },
  {
    id: "trans-care-bc",
    country: "ca",
    category: "healthcare",
    name: "Trans Care BC",
    url: "https://www.transcarebc.ca",
    description: {
      en: "Provincial healthcare support and transition information.",
    },
    languages: ["en"],
    lastReviewed: "2026-02-09",
  },
  {
    id: "egale-canada",
    country: "ca",
    category: "community",
    name: "Egale Canada",
    url: "https://egale.ca",
    description: {
      en: "National LGBTQIA+ advocacy organization.",
      fr: "Organisation nationale de défense des droits LGBTQIA+.",
    },
    languages: ["en", "fr"],
    lastReviewed: "2026-02-09",
  },
  {
    id: "astteq",
    country: "ca",
    category: "community",
    name: "ASTT(e)Q",
    url: "https://astteq.org",
    description: {
      en: "Support for trans communities in Quebec.",
      fr: "Soutien aux communautés trans au Québec.",
    },
    languages: ["en", "fr"],
    lastReviewed: "2026-02-09",
  },

  // ── 🇬🇧 United Kingdom ────────────────────────────────────────────
  {
    id: "nhs-gender-services",
    country: "uk",
    category: "healthcare",
    name: "NHS Gender Services",
    url: "https://www.nhs.uk",
    description: {
      en: "Information regarding NHS gender identity services.",
    },
    languages: ["en"],
    lastReviewed: "2026-02-09",
  },
  {
    id: "transactual-uk",
    country: "uk",
    category: "healthcare",
    name: "TransActual UK",
    url: "https://www.transactual.org.uk",
    description: {
      en: "Practical healthcare information and advocacy.",
    },
    languages: ["en"],
    lastReviewed: "2026-02-09",
  },
  {
    id: "gendered-intelligence",
    country: "uk",
    category: "community",
    name: "Gendered Intelligence",
    url: "https://genderedintelligence.co.uk",
    description: {
      en: "Support and education for trans communities.",
    },
    languages: ["en"],
    lastReviewed: "2026-02-09",
  },
  {
    id: "lgbt-foundation",
    country: "uk",
    category: "community",
    name: "LGBT Foundation",
    url: "https://lgbt.foundation",
    description: {
      en: "Health and wellbeing resources.",
    },
    languages: ["en"],
    lastReviewed: "2026-02-09",
  },
  {
    id: "mermaids",
    country: "uk",
    category: "community",
    name: "Mermaids",
    url: "https://mermaidsuk.org.uk",
    description: {
      en: "Support for trans and gender-diverse youth and families.",
    },
    languages: ["en"],
    lastReviewed: "2026-02-09",
  },
  {
    id: "switchboard-lgbt",
    country: "uk",
    category: "crisis",
    name: "Switchboard LGBT+",
    url: "https://switchboard.lgbt",
    description: {
      en: "Listening and support service.",
    },
    languages: ["en"],
    lastReviewed: "2026-02-09",
  },

  // ── 🇫🇷 France ────────────────────────────────────────────────────
  {
    id: "outrans",
    country: "fr",
    category: "community",
    name: "OUTrans",
    url: "https://outrans.org",
    description: {
      en: "French trans support and advocacy organization.",
      fr: "Association française de soutien et d'accompagnement des personnes trans.",
    },
    languages: ["fr"],
    lastReviewed: "2026-02-09",
  },
  {
    id: "acceptess-t",
    country: "fr",
    category: "community",
    name: "Acceptess-T",
    url: "https://www.acceptess-t.com",
    description: {
      en: "Support organization focused on trans communities.",
      fr: "Association de soutien aux communautés trans.",
    },
    languages: ["fr"],
    lastReviewed: "2026-02-09",
  },
  {
    id: "sos-homophobie",
    country: "fr",
    category: "crisis",
    name: "SOS Homophobie",
    url: "https://www.sos-homophobie.org",
    description: {
      en: "Support and reporting services.",
      fr: "Services de soutien et de signalement.",
    },
    languages: ["fr"],
    lastReviewed: "2026-02-09",
  },
  {
    id: "federation-lgbt",
    country: "fr",
    category: "community",
    name: "Fédération LGBT+",
    url: "https://www.federation-lgbt.org",
    description: {
      en: "Network of LGBTQ organizations across France.",
      fr: "Réseau d'associations LGBTQ à travers la France.",
    },
    languages: ["fr"],
    lastReviewed: "2026-02-09",
  },

  // ── 🇪🇸 Spain ─────────────────────────────────────────────────────
  {
    id: "felgtbi",
    country: "es",
    category: "community",
    name: "FELGTBI+",
    url: "https://felgtbi.org",
    description: {
      en: "National LGBTQ federation.",
      es: "Federación estatal LGTBI+.",
    },
    languages: ["es"],
    lastReviewed: "2026-02-09",
  },
  {
    id: "fundacion-triangulo",
    country: "es",
    category: "community",
    name: "Fundación Triángulo",
    url: "https://fundaciontriangulo.org",
    description: {
      en: "Support, advocacy, and educational resources.",
      es: "Apoyo, incidencia política y recursos educativos.",
    },
    languages: ["es"],
    lastReviewed: "2026-02-09",
  },
  {
    id: "chrysallis-espana",
    country: "es",
    category: "community",
    name: "Chrysallis España",
    url: "https://chrysallis.org.es",
    description: {
      en: "Support for trans youth and families.",
      es: "Apoyo a la infancia y juventud trans y a sus familias.",
    },
    languages: ["es"],
    lastReviewed: "2026-02-09",
  },

  // ── 🇲🇽 Mexico ────────────────────────────────────────────────────
  {
    id: "impulso-trans",
    country: "mx",
    category: "community",
    name: "Impulso Trans",
    url: "https://impulsotrans.org",
    description: {
      en: "Support and advocacy organization.",
      es: "Organización de apoyo e incidencia para personas trans.",
    },
    languages: ["es"],
    lastReviewed: "2026-02-09",
  },
  {
    id: "casa-de-las-munecas-tiresias",
    country: "mx",
    category: "community",
    name: "Casa de las Muñecas Tiresias",
    url: "https://casadelasmunecastiresias.org",
    description: {
      en: "Community support and social services.",
      es: "Apoyo comunitario y servicios sociales.",
    },
    languages: ["es"],
    lastReviewed: "2026-02-09",
  },
  {
    id: "red-de-juventudes-trans",
    country: "mx",
    category: "community",
    name: "Red de Juventudes Trans",
    url: "https://redjuventudestrans.org",
    description: {
      en: "Youth-focused trans support network.",
      es: "Red de apoyo enfocada en juventudes trans.",
    },
    languages: ["es"],
    lastReviewed: "2026-02-09",
  },

  // ── 🇧🇷 Brazil ────────────────────────────────────────────────────
  {
    id: "antra",
    country: "br",
    category: "healthcare",
    name: "ANTRA",
    url: "https://antrabrasil.org",
    description: {
      en: "National Association of Travestis and Transsexuals.",
      pt: "Associação Nacional de Travestis e Transexuais.",
    },
    languages: ["pt"],
    lastReviewed: "2026-02-09",
  },
  {
    id: "ibrat",
    country: "br",
    category: "community",
    name: "IBRAT",
    url: "https://ibrat.org",
    description: {
      en: "Brazilian Institute of Transmasculinities.",
      pt: "Instituto Brasileiro de Transmasculinidades.",
    },
    languages: ["pt"],
    lastReviewed: "2026-02-09",
  },
  {
    id: "casa-nem",
    country: "br",
    category: "community",
    name: "Casa Nem",
    url: "https://www.casanem.org",
    description: {
      en: "Housing, support, and advocacy services.",
      pt: "Moradia, acolhimento e serviços de incidência.",
    },
    languages: ["pt"],
    lastReviewed: "2026-02-09",
  },
  {
    id: "transempregos",
    country: "br",
    category: "employment",
    name: "TransEmpregos",
    url: "https://transempregos.com.br",
    description: {
      en: "Employment platform supporting transgender professionals.",
      pt: "Plataforma de emprego que apoia profissionais transgêneros.",
    },
    languages: ["pt"],
    lastReviewed: "2026-02-09",
  },

  // ── 🇦🇺 Australia ─────────────────────────────────────────────────
  {
    id: "transhub",
    country: "au",
    category: "healthcare",
    name: "TransHub",
    url: "https://www.transhub.org.au",
    description: {
      en: "Comprehensive gender-affirming healthcare information.",
    },
    languages: ["en"],
    lastReviewed: "2026-02-09",
  },
  {
    id: "acon",
    country: "au",
    category: "community",
    name: "ACON",
    url: "https://www.acon.org.au",
    description: {
      en: "Health promotion and community support.",
    },
    languages: ["en"],
    lastReviewed: "2026-02-09",
  },
  {
    id: "qlife",
    country: "au",
    category: "crisis",
    name: "QLife",
    url: "https://qlife.org.au",
    description: {
      en: "LGBTQ peer support and referral service.",
    },
    languages: ["en"],
    lastReviewed: "2026-02-09",
  },
  {
    id: "transcend-australia",
    country: "au",
    category: "community",
    name: "Transcend Australia",
    url: "https://transcend.org.au",
    description: {
      en: "Support for trans and gender-diverse youth and families.",
    },
    languages: ["en"],
    lastReviewed: "2026-02-09",
  },

  // ── 🇳🇿 New Zealand ──────────────────────────────────────────────
  {
    id: "gender-minorities-aotearoa",
    country: "nz",
    category: "community",
    name: "Gender Minorities Aotearoa",
    url: "https://genderminorities.com",
    description: {
      en: "Information and support resources.",
    },
    languages: ["en"],
    lastReviewed: "2026-02-09",
  },
  {
    id: "rainbowyouth",
    country: "nz",
    category: "community",
    name: "RainbowYOUTH",
    url: "https://ry.org.nz",
    description: {
      en: "Support for LGBTQIA+ youth.",
    },
    languages: ["en"],
    lastReviewed: "2026-02-09",
  },
  {
    id: "insideout-koaro",
    country: "nz",
    category: "community",
    name: "InsideOUT Kōaro",
    url: "https://insideout.org.nz",
    description: {
      en: "Education and support organization.",
    },
    languages: ["en"],
    lastReviewed: "2026-02-09",
  },

  // ── 🎙 Voice Training (global topic, not country-bound) ───────────
  {
    id: "trans-voice-lessons",
    country: "global",
    category: "voiceTraining",
    name: "Trans Voice Lessons",
    url: "https://www.youtube.com/@TransVoiceLessons",
    description: {
      en: "Free educational voice training content.",
      fr: "Contenu éducatif gratuit pour l'entraînement vocal.",
      es: "Contenido educativo gratuito de entrenamiento vocal.",
      pt: "Conteúdo educativo gratuito de treinamento vocal.",
    },
    languages: ["en"],
    lastReviewed: "2026-02-09",
  },
  {
    id: "seattle-voice-lab",
    country: "global",
    category: "voiceTraining",
    name: "Seattle Voice Lab",
    url: "https://www.seattlevoicelab.com",
    description: {
      en: "Professional voice training services.",
    },
    languages: ["en"],
    lastReviewed: "2026-02-09",
  },

  // ── 💼 Employment (global topic) ──────────────────────────────────
  {
    id: "out-in-tech",
    country: "global",
    category: "employment",
    name: "Out in Tech",
    url: "https://outintech.com",
    description: {
      en: "Career development and networking.",
    },
    languages: ["en"],
    lastReviewed: "2026-02-09",
  },
  {
    id: "startout",
    country: "global",
    category: "employment",
    name: "StartOut",
    url: "https://startout.org",
    description: {
      en: "Entrepreneurship and career support for LGBTQ founders.",
    },
    languages: ["en"],
    lastReviewed: "2026-02-09",
  },
  {
    id: "mygwork",
    country: "global",
    category: "employment",
    name: "myGwork",
    url: "https://mygwork.com",
    description: {
      en: "Inclusive employment and job opportunities.",
    },
    languages: ["en"],
    lastReviewed: "2026-02-09",
  },
];

/**
 * Pick the best-available description for a given locale.
 * Falls back to English when the requested locale isn't authored —
 * this is intentional. Authoring all 4 locales × 45 orgs at once is
 * busywork; we add translations as the founder + community supply
 * them, and never block on missing copy.
 */
export const resolveDescription = (resource, locale) => {
  const lang = (locale || "en").split("-")[0];
  return (
    resource.description[lang] || resource.description.en || ""
  );
};
